/**
 * src/modules/legacy-status-tree.ts — Sprint 20.2 Etap 9 (2026-05-03 SESSION 22).
 *
 * Status tree caching + color mapping — wyciągnięte z app.js.
 *
 * Eksportuje:
 *   - _ensureStatusTreeMap() — async fetch /api/order-status/tree, cache do window.__sbStateExt
 *   - statusColorForLabel(label) → 'info'|'primary'|'warning'|'success'|'error'
 *
 * Cache state: w `window.__sbStateExt` (shared z extracted modules).
 *
 * Strangler: window expose tylko gdy app.js IIFE nie zdefiniowała.
 */

let _statusTreeMap: Record<string, any> | null = null;
let _paymentStatusTreeMap: Record<string, any> | null = null;
let _shipmentStatusTreeMap: Record<string, any> | null = null;

function _w(): any { return window as any; }

function _state(): any {
  const w = _w();
  return (w.SellBaseApp && w.SellBaseApp.state) || w.state || {};
}

export function _ensureStatusTreeMap(): void {
  if (_statusTreeMap && _paymentStatusTreeMap && _shipmentStatusTreeMap) return;
  const w = _w();
  if (!w.SellBaseAPI || typeof w.SellBaseAPI.getOrderStatusTree !== 'function') return;

  w.SellBaseAPI.getOrderStatusTree().then((all: any[]) => {
    _statusTreeMap = {};
    _paymentStatusTreeMap = {};
    _shipmentStatusTreeMap = {};
    // 2026-05-13 fix: preferuj systemowe statusy (is_system=1) gdy nameKey collide.
    // Wcześniej: drag&drop dodawał allegro_new (name="Nowe", color #f97316) → nadpisywało
    // wpis 'nowe' z systemowego `new` (#3b82f6 niebieski). W rezultacie WSZYSTKIE statusy
    // w UI dropdown stawały się pomarańczowo-czerwone.
    // Plus: sortujemy by is_system=1 leciał pierwszy (key zawsze unique, nameKey może collide).
    const _sortedAll = (all || []).slice().sort((a: any, b: any) => {
      const sa = (a.is_system ? 0 : 1);
      const sb = (b.is_system ? 0 : 1);
      return sa - sb;  // is_system=1 idzie pierwsze
    });
    _sortedAll.forEach((s: any) => {
      const key = (s.key || '').toLowerCase();
      const nameKey = (s.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const cat = s.category || 'order';
      const targetMap =
        cat === 'order' ? _statusTreeMap! :
        cat === 'payment' ? _paymentStatusTreeMap! :
        cat === 'shipment' ? _shipmentStatusTreeMap! :
        null;
      if (!targetMap) return;
      // Key (s.key) jest unique — zawsze nadpisuj
      targetMap[key] = s;
      // nameKey może collide — wpis tylko jeśli jeszcze pusty (pierwszy = systemowy wygrywa)
      if (nameKey && !targetMap[nameKey]) targetMap[nameKey] = s;
    });
    // GRUPY STATUSOW (2026-08-10) — czyste szuflady, pobierane razem z drzewkiem.
    // Nie sa statusami, wiec nie moga siedziec w mapie statusow; maja wlasne miejsce.
    fetch('/api/order-status-groups', {
      headers: { Authorization: 'Bearer ' + ((w.SellBaseAPI && w.SellBaseAPI.getAuthToken) ? w.SellBaseAPI.getAuthToken() : '') },
    }).then(function (r) { return r.ok ? r.json() : { groups: [] }; })
      .then(function (d) {
        w.__sbStateExt = w.__sbStateExt || {};
        w.__sbStateExt._statusGroups = (d && d.groups) || [];
        var st2 = _state();
        if (st2.currentView === 'orders' && typeof w._renderOrdersStatusPanel === 'function') {
          w._renderOrdersStatusPanel();
        }
      })
      .catch(function () {
        w.__sbStateExt = w.__sbStateExt || {};
        w.__sbStateExt._statusGroups = [];
      });
    // 0245: OSOBISTE nadpisania widoku grup (⊞/🏷 w pasku zamówień) — flagi na
    // grupie to tylko domyślka firmowa, prawdziwy widok usera siedzi w user_ui_prefs.
    fetch('/api/user-prefs/widok_grup', {
      headers: { Authorization: 'Bearer ' + ((w.SellBaseAPI && w.SellBaseAPI.getAuthToken) ? w.SellBaseAPI.getAuthToken() : '') },
    }).then(function (r) { return r.ok ? r.json() : { value: null }; })
      .then(function (d) {
        w.__sbStateExt = w.__sbStateExt || {};
        w.__sbStateExt._widokGrupUser = (d && d.value) || {};
        var st3 = _state();
        if (st3.currentView === 'orders' && typeof w._renderOrdersStatusPanel === 'function') {
          w._renderOrdersStatusPanel();
        }
      })
      .catch(function () {
        w.__sbStateExt = w.__sbStateExt || {};
        w.__sbStateExt._widokGrupUser = w.__sbStateExt._widokGrupUser || {};
      });

    // Mirror to window.__sbStateExt for extracted modules
    if (!w.__sbStateExt) w.__sbStateExt = {};
    w.__sbStateExt._statusTreeMap = _statusTreeMap;
    w.__sbStateExt._paymentStatusTreeMap = _paymentStatusTreeMap;
    w.__sbStateExt._shipmentStatusTreeMap = _shipmentStatusTreeMap;
    // Re-render status panel jeśli widok aktywny
    const state = _state();
    if (state.currentView === 'orders' && typeof w._renderOrdersStatusPanel === 'function') {
      w._renderOrdersStatusPanel();
    }
  }).catch(() => {
    _statusTreeMap = {};
    _paymentStatusTreeMap = {};
    _shipmentStatusTreeMap = {};
    if (!w.__sbStateExt) w.__sbStateExt = {};
    w.__sbStateExt._statusTreeMap = _statusTreeMap;
    w.__sbStateExt._paymentStatusTreeMap = _paymentStatusTreeMap;
    w.__sbStateExt._shipmentStatusTreeMap = _shipmentStatusTreeMap;
  });
}

/** Unieważnia trzy mapy po CRUD drzewka i opcjonalnie pobiera je ponownie. */
export function _invalidateStatusTreeMap(reload: boolean = true): void {
  _statusTreeMap = null;
  _paymentStatusTreeMap = null;
  _shipmentStatusTreeMap = null;
  const w = _w();
  if (!w.__sbStateExt) w.__sbStateExt = {};
  delete w.__sbStateExt._statusTreeMap;
  delete w.__sbStateExt._paymentStatusTreeMap;
  delete w.__sbStateExt._shipmentStatusTreeMap;
  if (reload) _ensureStatusTreeMap();
}

export function statusColorForLabel(label: string): 'info' | 'primary' | 'warning' | 'success' | 'error' {
  // 1. Try z drzewka (cache)
  const w = _w();
  const treeMap = _statusTreeMap || (w.__sbStateExt && w.__sbStateExt._statusTreeMap);
  if (treeMap) {
    const key = (label || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const found = treeMap[key];
    if (found && found.color) {
      const c = found.color;
      if (c === '#3b82f6') return 'info';
      // 2026-05-13: #f97316 (pomarańczowy, np. status "Zwrot") → 'warning', nie 'error'.
      // Wcześniej był grupowany z #ef4444 czerwonym → wszystkie statusy Allegro z #f97316
      // wyglądały tak samo jak Anulowane (czerwone).
      if (c === '#f59e0b' || c === '#f97316') return 'warning';
      if (c === '#22c55e' || c === '#10b981' || c === '#14b8a6') return 'success';
      if (c === '#ef4444') return 'error';
      if (c === '#6366f1' || c === '#8b5cf6') return 'primary';
      return 'info';
    }
  }
  // 2. Fallback hardcoded mapping
  const map: Record<string, 'info' | 'primary' | 'warning' | 'success' | 'error'> = {
    'Nowe': 'info',
    'Potwierdzone': 'primary',
    'W realizacji': 'warning',
    'Spakowane': 'info',
    'Wysłane': 'primary',
    'Dostarczone': 'success',
    'Do wysłania': 'primary',
    'Do odbioru': 'info',
    'Odebrane': 'success',
    'Anulowane': 'error',
    'Zwrot': 'error',
    'Gotowe do odbioru': 'info',
  };
  return map[label] || 'info';
}

if (typeof window !== 'undefined') {
  const w = _w();
  if (typeof w._ensureStatusTreeMap !== 'function') w._ensureStatusTreeMap = _ensureStatusTreeMap;
  if (typeof w._invalidateStatusTreeMap !== 'function') w._invalidateStatusTreeMap = _invalidateStatusTreeMap;
  if (typeof w.statusColorForLabel !== 'function') w.statusColorForLabel = statusColorForLabel;
  // Extended strangler: __sbExports
  if (!w.__sbExports) w.__sbExports = {};
  if (typeof w.__sbExports._ensureStatusTreeMap !== 'function') w.__sbExports._ensureStatusTreeMap = _ensureStatusTreeMap;
  if (typeof w.__sbExports._invalidateStatusTreeMap !== 'function') w.__sbExports._invalidateStatusTreeMap = _invalidateStatusTreeMap;
  if (typeof w.__sbExports.statusColorForLabel !== 'function') w.__sbExports.statusColorForLabel = statusColorForLabel;
}
